import { Percent } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

import { useRequiredFieldHighlight } from "../hooks/useRequiredFieldHighlight";
import type { ContractFormValues } from "../schemas/contractFormSchema";

import { FormMessageT } from "./FormMessageT";
import { FormSectionCard } from "./FormSectionCard";
import { InfoTooltip } from "./InfoTooltip";

const RATE_TYPES = ["IBR", "IMPLICIT_RATE"] as const;

/** Spec §11.15.6 — universal input chrome (visible borders in both themes). */
const INPUT_CHROME = "bg-background border border-input shadow-sm";

/** Spec §11.15.2 — pixel-perfect label container. */
const LABEL_FLEX =
  "flex items-center gap-1.5 text-sm font-medium text-foreground/90";

export function DiscountRateSection() {
  const { t } = useTranslation();
  const form = useFormContext<ContractFormValues>();
  const rateHighlight = useRequiredFieldHighlight("discountRateAnnual");

  return (
    <FormSectionCard
      title={t("form.section.discountRate")}
      description={t("form.section.discountRateDescription")}
      icon={<Percent className="h-4 w-4" />}
    >
      {/* IFRS 16 §26: determine the rate TYPE (implicit vs IBR) first, then the rate. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
        <FormField
          control={form.control}
          name="discountRateType"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.discountRateType")}
                <span className="text-destructive">*</span>
                <InfoTooltip
                  content={t("form.discountRateTypeHelp")}
                  ariaLabel={t("form.discountRateTypeHelpAria")}
                  data-testid="tooltip-discount-rate-type"
                />
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="grid grid-cols-1 gap-2 pt-1"
                >
                  {RATE_TYPES.map((rt) => {
                    const id = `rate-${rt.toLowerCase()}`;
                    const active = field.value === rt;
                    return (
                      <label
                        key={rt}
                        htmlFor={id}
                        className={cn(
                          "flex items-center gap-2 rounded-lg border p-3 cursor-pointer transition-colors",
                          active
                            ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20"
                            : "border-border hover:bg-muted/40",
                        )}
                      >
                        <RadioGroupItem value={rt} id={id} />
                        <span className="text-sm font-medium">
                          {t(`discountRateType.${rt}`)}
                        </span>
                      </label>
                    );
                  })}
                </RadioGroup>
              </FormControl>
              <FormMessageT />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="discountRateAnnual"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={LABEL_FLEX}>
                {t("form.discountRateAnnual")}
                <span className="text-destructive">*</span>
                <InfoTooltip
                  content={t("form.discountRateAnnualHelp")}
                  ariaLabel={t("form.discountRateAnnualHelpAria")}
                  data-testid="tooltip-discount-rate-annual"
                />
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type="number"
                    min={0.01}
                    max={500}
                    step={0.01}
                    inputMode="decimal"
                    {...field}
                    value={field.value ?? ""}
                    className={cn(
                      INPUT_CHROME,
                      rateHighlight,
                      "text-right pr-8 font-mono tabular-nums",
                    )}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    %
                  </span>
                </div>
              </FormControl>
              <FormMessageT />
            </FormItem>
          )}
        />
      </div>
    </FormSectionCard>
  );
}
